import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DetectRTC from "detectrtc";
import AllRoutes from "./routes";

import "react-intl-tel-input/dist/main.css";
import "react-multi-email/dist/style.css";

import "./assets/scss/variables.scss";
import "./assets/scss/icomoon.scss";
import "./assets/scss/index.scss";
import "./assets/scss/annotation.scss";
import "./assets/scss/gallery-view.scss";
import "./components/UploadFiles/UploadFiles.scss";

import "./components/Notes/Note.scss";
import "./components/Bookmarks/Bookmark.scss";
import "./components/Chat/Chat.scss";
import "./components/ParticipantsList/ParticipantsList.scss";
import "./components/AskAI/askAI.scss";
import "./components/CallScreens/CallScreens.scss";
import "./components/BottomMenuBar/BottomMenuBar.scss";
import "./components/CallScreens/VideoCallScreen/VideoScreen/VideoScreen.scss";
import "./components/CallScreens/VideoCallScreen/WaitingScreen/WaitingScreen.scss";

import "bootstrap/dist/js/bootstrap.min.js";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { checkAndroidOS, decideDeviceType, getDomainURL, getLanguageFromStorage } from "./shared/utility";
import { checkIsCompanyAvailable } from "./actions/onLoadFunctionAction";
import { setLanguagesInDataReducer } from "./actions/languageAction";
import { HELP_ARTICLE, SOLUTION_ARTICLE_LINK, ARTICLES_NUMBER } from "./constants/LDConstants";
import { GLOBAL_MEETING_DATA, PublicVariables } from "./constants/PublicVariables";
import { isCustomDomain, isGuestAvailable, setShowLoading, setShowMeBackground, setUserType } from "./shared/function";
import navigatorService from "./services/navigatorService";
import { checkNetworkType } from "./components/Network/NetworkUtils";
import { TWILLIO_URL_ENDPOINT, TWILLIO_URL_ENDPOINT_FOR_XIAOMI } from "./constants/Constants";

const App = () => {
    const { isCompanyAvailableData } = useSelector((state) => state.onLoadData);
    const dispatch = useDispatch();

    // NOTE: LaunchDarkly is now initialized conditionally inside CallScreens
    // after domainId exists to prevent anonymous user creation.
    // The logic that was here using ldFlags has been moved to CallScreens.
    const checkCompanyInfo = () => {
        dispatch(
            checkIsCompanyAvailable({
                domainURL: getDomainURL() ?? "",
            })
        );
    };

    useEffect(() => {
        if (process.env.REACT_APP_APP_ENV === "production") {
            // production env
            console.log = function () {
                // This is intentional
            };
            console.error = function () {
                // This is intentional
            };
            console.info = function () {
                // This is intentional
            };
            console.warn = function () {
                // This is intentional
            };
        }
        if (isCustomDomain()) {
            // call api when custom url and host mapping url to check
            checkCompanyInfo();
        }
        // Set values in localStorage
        localStorage.setItem("storagetype", "localStorage");
        localStorage.setItem("keepme", "true");
        PublicVariables.isMobile = navigatorService.isMobileDeviceOrIpadOs() || navigatorService.mobileIpad();
        PublicVariables.isIOSDevice = navigatorService.isIosDevice();
        setShowLoading(true);
        setUserType(dispatch);

        DetectRTC.load(() => {});

        /* Handle a global domain with no session id in params */
        if (!GLOBAL_MEETING_DATA?.isCustomDomain && [TWILLIO_URL_ENDPOINT, TWILLIO_URL_ENDPOINT_FOR_XIAOMI].includes(window?.location?.pathname)) {
            setShowMeBackground();
        }
        decideDeviceType(); // set device type based on the Desktop, Android, and iOS
        checkNetworkType(); // check network type on load
        isGuestAvailable(); // add body-guest class if the user is guest
        checkAndroidOS();
    }, []);

    useEffect(() => {
        if (isCompanyAvailableData) {
            const favicon = document.getElementById("favicon");
            if (isCompanyAvailableData?.faviconImage) {
                favicon.setAttribute("href", process.env.REACT_APP_FAVICON_URL + isCompanyAvailableData.faviconImage);
            }
            getLanguageFromStorage();
            dispatch(setLanguagesInDataReducer(isCompanyAvailableData));
        }
    }, [isCompanyAvailableData]);

    // Help article links setup - moved from LD flags
    useEffect(() => {
        const helpArticleLink = SOLUTION_ARTICLE_LINK;

        HELP_ARTICLE.RESET_GUIDE_LINK = helpArticleLink + ARTICLES_NUMBER.LOCATION_PERMISSION_ARTICLES_NUMBER;
        HELP_ARTICLE.BROWSER_REQUIRED_TO_CONNECT_CALL = helpArticleLink + ARTICLES_NUMBER.BROWSER_REQUIRED_TO_CONNECT_CALL_ARTICLES_NUMBER;
        HELP_ARTICLE.COMMON_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.COMMON_ARTICLES_NUMBER;
        HELP_ARTICLE.FIREFOX_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.FIREFOX_ARTICLES_NUMBER;
        HELP_ARTICLE.SAFARI_MAC_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.SAFARI_MAC_ARTICLES_NUMBER;
        HELP_ARTICLE.SAFARI_MOBILE_TAB_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.SAFARI_MOBILE_TAB_ARTICLES_NUMBER;
        HELP_ARTICLE.EDGE_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.EDGE_ARTICLES_NUMBER;
        HELP_ARTICLE.SAMSUNG_INTERNET_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.SAMSUNG_INTERNET_ARTICLES_NUMBER;
        HELP_ARTICLE.MOBILE_CHROME_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.MOBILE_CHROME_ARTICLES_NUMBER;
        HELP_ARTICLE.DESKTOP_CHROME_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.DESKTOP_CHROME_ARTICLES_NUMBER;
        HELP_ARTICLE.IOS_APP_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.IOS_APP_ARTICLES_NUMBER;
        HELP_ARTICLE.ANDROID_APP_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.ANDROID_APP_ARTICLES_NUMBER;
        HELP_ARTICLE.MEASUREMENT_APP_ARTICLES_NUMBER = helpArticleLink + ARTICLES_NUMBER.MEASUREMENT_APP_ARTICLES_NUMBER;
    }, []);

    return <AllRoutes />;
};

export default App;
